import { useCallback, useEffect, useRef } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import { AsyncSelectField, SelectOption } from '../common/AsyncSelectField';
import { fetchAdoResource } from '../common/adoResourcesApi';

type AzureDevOpsAgentPool = {
  id: number;
  name: string;
};

type TemplateFormData = {
  projectName?: string;
};

export const AzureDevOpsAgentPoolPicker = ({
  onChange,
  formData,
  required,
  rawErrors,
  formContext,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const projectName = (formContext.formData as TemplateFormData | undefined)
    ?.projectName;

  const previousProjectName = useRef<string | undefined>(projectName);

  useEffect(() => {
    if (
      previousProjectName.current !== undefined &&
      previousProjectName.current !== projectName
    ) {
      onChange('');
    }

    previousProjectName.current = projectName;
  }, [projectName, onChange]);

  const loadAgentPools = useCallback(async (): Promise<SelectOption[]> => {
    if (!projectName) {
      return [];
    }

    const pools = await fetchAdoResource<AzureDevOpsAgentPool[]>(
      discoveryApi,
      fetchApi,
      `/projects/${encodeURIComponent(projectName)}/agent-pools`,
    );

    return pools.map(pool => ({
      value: pool.name,
      label: pool.name,
    }));
  }, [discoveryApi, fetchApi, projectName]);

  return (
    <AsyncSelectField
      label={schema.title ?? 'Infrastructure Agent Pool'}
      description={schema.description}
      value={formData}
      required={required}
      rawErrors={rawErrors}
      onChange={onChange}
      loadOptions={loadAgentPools}
      disabled={!projectName}
      dependency={projectName}
      disabledText="Select an Azure DevOps project first"
      loadingText="Loading agent pools..."
      emptyText="Select an agent pool"
    />
  );
};
